import React from 'react';
import BEM from '@upsales/components/Utils/bemClass';
import { Flex, Icon, Text, type IconName } from '@upsales/components';
import { useTranslation } from 'Components/Helpers/translate';
import AppService from 'App/legacy/AppService';
import { getDialPropsFromState } from 'App/services/VoiceService';
import moment from 'moment';
import type { ActionableStep } from './types';

import './OutreachStepRow.scss';

const CHANNEL_ICON: Record<string, IconName> = {
	email: 'envelope',
	call: 'phone',
	linkedin: 'linkedin',
	todo: 'check-square-o'
};

type Props = {
	step: ActionableStep;
	busy: boolean;
	expanded: boolean;
	onToggle: () => void;
	onSend: () => void;
	onDone: () => void;
};

/**
 * One approved reach, as the thing to do rather than a to-do to read (WEB-8739).
 *
 * Clicking the row does what the channel implies: an email opens the composer (same as
 * its button — there's nothing to expand, the draft is the email), anything else expands
 * to show the full brief, because a call's talking points are the work and three clamped
 * lines aren't enough to make the call from.
 */
const OutreachStepRow = ({ step, busy, expanded, onToggle, onSend, onDone }: Props) => {
	const { t } = useTranslation();
	const CSS = new BEM('OutreachStepRow');
	const isEmail = step.channel === 'email';

	// Reuse the review page's channel keys, falling back to the raw value.
	const channelLabel = (): string => {
		const key = `prospectingAgent.channel.${step.channel}`;
		const translated = t(key);
		return translated && translated !== key ? translated : step.channel;
	};

	const action = (): React.ReactNode => {
		const actionClass = CSS.elem('action').b();
		if (isEmail && step.contactEmail) {
			return (
				<button
					type="button"
					className={actionClass}
					disabled={busy}
					onClick={e => {
						e.stopPropagation();
						onSend();
					}}
				>
					{t('prospectingAgent.sendEmail')}
				</button>
			);
		}
		if (step.channel === 'call' && step.contactPhone) {
			// Read off AppService rather than redux: the react-driver mounts these cards
			// outside the store provider.
			const params = AppService.getMetadata()?.params;
			const dial =
				params &&
				getDialPropsFromState(
					step.contactPhone,
					{
						type: 'contact',
						record: {
							id: step.contactId,
							name: step.contactName ?? '',
							client: { id: step.clientId, name: step.clientName ?? '' }
						}
					},
					{ phoneIntegration: AppService.getPhoneIntegration(), params },
					// Log the call against the to-do this step scheduled.
					step.activityId ? { type: 'Activity', id: step.activityId } : undefined
				);
			if (dial) {
				return (
					<a
						className={actionClass}
						href={dial.href}
						target={dial.target}
						onClick={e => {
							e.stopPropagation();
							dial.onClick(e);
						}}
					>
						{t('prospectingAgent.callContact')}
					</a>
				);
			}
		}
		if (step.channel === 'linkedin' && step.contactLinkedin) {
			return (
				<a
					className={actionClass}
					href={step.contactLinkedin}
					target="_blank"
					rel="noopener noreferrer"
					onClick={e => e.stopPropagation()}
				>
					{t('prospectingAgent.openLinkedin')}
				</a>
			);
		}
		// Research briefs, and any step whose contact lost the address/number its
		// channel needs: the brief is the work, so there's nothing to trigger.
		return null;
	};

	return (
		<div
			className={CSS.mod({ expanded }).b()}
			onClick={() => (isEmail && step.contactEmail ? onSend() : onToggle())}
		>
			<Flex justifyContent="space-between" alignItems="flex-start" gap={8}>
				<Flex alignItems="flex-start" gap={10} className={CSS.elem('main').b()}>
					<Icon name={CHANNEL_ICON[step.channel] || 'circle'} color="grey-10" space="mts" />
					<div className={CSS.elem('text').b()}>
						<Text size="sm" bold={true} ellipsis={true}>
							{step.subject || channelLabel()}
						</Text>
						<Text size="sm" color="grey-10" ellipsis={true}>
							{/* The contact is a link out to their card — a rep reading the row
							    usually wants the person, not just their name (WEB-8739). */}
							<a
								href={`#/${AppService.getCustomerId()}/contacts/${step.contactId}/`}
								onClick={e => e.stopPropagation()}
							>
								{step.contactName}
							</a>
							{step.contactTitle ? ` · ${step.contactTitle}` : ''}
							{step.activityDate ? ` · ${moment(step.activityDate).format('L')}` : ''}
						</Text>
					</div>
				</Flex>
				<Flex alignItems="center" gap={4} className={CSS.elem('actions').b()}>
					{action()}
					<button
						type="button"
						className={CSS.elem('done').b()}
						disabled={busy}
						onClick={e => {
							e.stopPropagation();
							onDone();
						}}
					>
						{t('prospectingAgent.markStepDone')}
					</button>
				</Flex>
			</Flex>
			{step.body ? (
				<Text size="sm" color="grey-11" className={CSS.elem('draft').mod({ expanded }).b()}>
					{step.body}
				</Text>
			) : null}
		</div>
	);
};

export default OutreachStepRow;
